import {DisplayState} from './display-state';
import {AutoClearSetting} from './alarm';
import {CustomContext} from './custom-context';
import {TileFolderDisplayMode, TileId} from './tile';
import {TileIcon} from './tile-icon';
import {Protocol, PROTOCOL_FORMULA} from './protocol';
import { DeviceId } from './device';
import { CompareOperator } from '../constants/tile-config.constants';

export interface BaseStructure {
  CustomContext?: CustomContext;
  // Protocol: TileProtocol;
  Priority: number;
}

export interface SimpleStructure extends BaseStructure {
  DeviceID: DeviceId;
  Writable: boolean;
  PointID: string;
  AutoClear: AutoClearSetting;
  Protocol: Protocol;
  Priority: number;

  // [x: string]: any;
}

export interface HasConditionsStructure extends SimpleStructure {
  Conditions: Condition[];
  AlarmIntegrations?: string[];
}

export interface SimpleBoolStructure extends HasConditionsStructure {
  DisplayTrue: string;
  DisplayFalse: string;
}

export interface SimpleAnalogStructure extends HasConditionsStructure {
  DisplayRawUnits: string;
}

export interface SimpleMultistateStructure extends HasConditionsStructure {
  BuiltInStates: string[];
  DisplayStates: DisplayState[];
}

export interface SuperFolderStructure extends BaseStructure {
  DisplayMode: TileFolderDisplayMode;
  TileIDs: TileId[];
  DataPoints?: DataPoint[];
}

export interface DataPoint {
  TileID: TileId,
  Hidden: boolean
}

export interface SuperTreeStructure extends BaseStructure {
}

export class CanvasPoint {
  TileIDs: TileId[]; // Array of tile IDs (single tile is array with one element)
  X: number; // normalized 0.0-1.0
  Y: number; // normalized 0.0-1.0

  static getId(point: CanvasPoint): string {
    return `${point.X},${point.Y}`;
  }

  static getIds(points: CanvasPoint[]): string[] {
    const seen = new Map<string, number>();
    return points.map(point => {
      const base = CanvasPoint.getId(point);
      const count = seen.get(base) ?? 0;
      seen.set(base, count + 1);
      return count === 0 ? base : `${base}#${count}`;
    });
  }
}

export interface CanvasInitialView {
  CenterX: number; // normalized 0.0–1.0
  CenterY: number; // normalized 0.0–1.0
  Zoom: number;    // Leaflet zoom level (negative for L.CRS.Simple)
}

export interface CanvasClusterSettings {
  Enabled: boolean;
}

export interface CanvasStructure extends BaseStructure {
  Image?: string; // file upload UUID
  Points: CanvasPoint[];
  Cluster?: CanvasClusterSettings; // whether overlapping markers should be grouped into cluster badges
  InitialView?: CanvasInitialView; // persisted viewport; absent means fit full image on load
}

export interface CompareFunc {
  Operator: CompareOperator;
  Value: number;
}

export interface Condition {
  Enabled: boolean;
  Mode: ConditionMode;
  Comparison: CompareFunc;
  DisplayText: string;
  Icon: TileIcon | '';
  Delay: number;
}

export enum ConditionMode {
  None = '',
  Warning = 'warning',
  Critical = 'critical'
}

export interface FormulaStructure extends BaseStructure {
  Protocol: typeof PROTOCOL_FORMULA;
  FormulaType: string;
  Inputs: Record<string, TileId>;
}

export type TileStructure =
  SimpleBoolStructure |
  SimpleAnalogStructure |
  SimpleMultistateStructure |
  SuperFolderStructure |
  SuperTreeStructure |
  CanvasStructure |
  FormulaStructure;
